import React from "react";
import styles from "./view/PlaydateInviteRecipientView.module.css";
import { FamilySummary, InviteStatus, PlaydateGuestType, PlaydateInvite, User, UserSummary } from "../../gql/types";
import TimeSelectionSelectionHeader from "../time/TimeSelectionSelectionHeader";
import TimeSelectionDisplayView from "../time/TimeSelectionDisplayView";
import { convertScheduleOptionsToDayPartSelections } from "../../service/playdate/converters/converter";
import ParticipantSummaryRow from "./ParticipantSummaryRow";
import { ParticipantRowWithOthers } from "../profile/ParticipantRowWithOthers";
import { ProfileRowPlacementType } from "../profile/ProfileDetailRow";

interface Props {
    user?: User;
    invite: PlaydateInvite;
}

const PlaydateInviteConfirmedDetails: React.FC<Props> = ({ user, invite }) => {
    if (invite.status !== InviteStatus.Confirmed || (!invite.finalScheduleOption || invite.finalScheduleOption === null)) {
        return <></>;
    }

    let representedPerson: UserSummary | FamilySummary | undefined;

    const isFamilyInvite = invite.guestType === PlaydateGuestType.Family;

    if (user) {
        if (isFamilyInvite) {
            representedPerson = user.family !== null ? user.family : {
                name: "External User",
            } as FamilySummary;
        } else {
            representedPerson = {
                firstName: user.firstName,
                lastName: user.lastName,
                profilePictureUrl: user.profilePictureUrl,
                id: user.id,
            } as UserSummary;
        }
    }

    if (!representedPerson) {
        if (invite.guests && invite.guests.length > 0) {
            representedPerson = invite.guests[0];
        } else {
            if (invite.externalGuests && invite.externalGuests.length > 0) {
                const externalGuest = invite.externalGuests[0];
                representedPerson = {
                    firstName: externalGuest.name,
                } as UserSummary;
            } else {
                // assume the user sent it out an external user family.
                representedPerson = {
                    firstName: "External User",
                } as UserSummary;
            }
        }
    }

    return (
        <>
            <div className={styles.sectionHeader}>
                <TimeSelectionSelectionHeader
                    key={"timeSelectionHeader"}
                    header={"Dates & Times"}
                />
            </div>
            <TimeSelectionDisplayView
                mode={"daypart"}
                groupByDate={true}
                daypartIconSelected={true}
                selectedDayParts={convertScheduleOptionsToDayPartSelections(invite.finalScheduleOption)}
            />
            <div className={styles.sectionHeader}>Host:</div>
            <ParticipantSummaryRow
                participant={invite.organizer}
                placement={ProfileRowPlacementType.STANDALONE}
                isReadOnly={true}
            />

            <div className={styles.sectionHeader}>Guests:</div>
            <ParticipantRowWithOthers
                mainParticipant={representedPerson}
                otherParticipants={invite.guests !== null ? (invite.guests ?? []) : []}
            />
            <div className={styles.sectionHeader}>Title:</div>
            <div className={styles.title}>
                {invite.title}
            </div>
            <div className={styles.sectionHeader}>Location:</div>
            <div className={styles.title}>
                {invite.location}
            </div>

            <div className={styles.sectionHeader}>Details:</div>
            <div className={styles.notes}>
                {invite.details || <em>No message provided</em>}
            </div>
        </>
    );
};

export default PlaydateInviteConfirmedDetails;
